function hexToRgb(hex: string): [number, number, number] | null {
  let value = hex.trim();
  const short = value.match(/^#([0-9a-f]{3})$/i);
  if (short) {
    return [
      parseInt(short[1][0] + short[1][0], 16),
      parseInt(short[1][1] + short[1][1], 16),
      parseInt(short[1][2] + short[1][2], 16),
    ];
  }
  const full = value.match(/^#([0-9a-f]{6})$/i);
  if (!full) return null;
  return [
    parseInt(full[1].slice(0, 2), 16),
    parseInt(full[1].slice(2, 4), 16),
    parseInt(full[1].slice(4, 6), 16),
  ];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (raw: number) => {
    const v = raw / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastWithWhite(rgb: [number, number, number]): number {
  return 1.05 / (relativeLuminance(rgb) + 0.05);
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h /= 6;
  }
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

function toHex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

/** Oscurece un color de acento preservando su tono hasta alcanzar contraste AA
 *  sobre superficies claras. Los colores de los temas oscuros suelen ser
 *  luminosos y resultan ilegibles sobre tarjetas blancas del tema claro. */
export function readableOnLight(hex: string, target = 4.5): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  if (contrastWithWhite(rgb) >= target) return hex;
  let [h, s, l] = rgbToHsl(...rgb);
  let current = rgb;
  let guard = 0;
  while (contrastWithWhite(current) < target && l > 0 && guard < 1000) {
    l = Math.max(0, l - 0.005);
    current = hslToRgb(h, s, l);
    guard += 1;
  }
  return toHex(current);
}

/** Devuelve el color ajustado si el tema activo es claro; en oscuro conserva
 *  el valor original. */
export function toneForTheme(hex: string, theme: 'light' | 'dark'): string {
  return theme === 'light' ? readableOnLight(hex) : hex;
}
